import React, { useState, useEffect } from 'react';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { Link, useLocation } from 'react-router-dom';
import { navigation, contact, images } from '../../content';
import { getWhatsAppLink } from '../../content/contentUtils';

// Valores por defecto
const defaultNavLinks = [
  { name: 'Inicio', href: '#inicio', isExternal: false },
  { name: 'Contacto', href: '#contacto', isExternal: false },
];

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  
  // Obtener enlaces de navegación del contenido o usar defaults
  const navLinks = navigation?.links || defaultNavLinks;
  const ctaButtonText = navigation?.ctaButton || "Solicitar crédito";
  const phoneNumber = contact?.phone || "(656) 536-7364";
  const logoDefault = images?.logo || "/images/logo-confiare.png";
  const logoWhite = images?.logoWhite || "/images/logo-confiare-blanco.png";
  
  // Check if we're on a subpage (not home)
  const isSubPage = location.pathname !== '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // On subpages, always show scrolled style
  const showScrolledStyle = isScrolled || isSubPage;

  const scrollToSection = (e, href) => {
    e.preventDefault();
    // If on subpage, navigate to home first
    if (isSubPage) {
      window.location.href = '/' + href;
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showScrolledStyle
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - links to home */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0">
            <img 
              src={showScrolledStyle ? logoDefault : logoWhite}
              alt="CONFIARÉ Logo" 
              className="h-10 w-auto transition-all duration-300"
            />
          </Link>

          {/* Desktop Navigation - Centered with button style */}
          <nav className="hidden lg:flex items-center justify-center flex-1 mx-8">
            <div className={`flex items-center gap-1 px-2 py-1.5 rounded-full ${
              showScrolledStyle 
                ? 'bg-gray-100/80' 
                : 'bg-white/10 backdrop-blur-sm'
            }`}>
              {navLinks.map((link) => (
                link.isExternal ? (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      showScrolledStyle
                        ? 'text-gray-700 hover:text-[#013C5D] hover:bg-white'
                        : 'text-white/90 hover:text-white hover:bg-white/20'
                    }`}
                  >
                    {link.name}
                  </Link>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => scrollToSection(e, link.href)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      showScrolledStyle
                        ? 'text-gray-700 hover:text-[#013C5D] hover:bg-white'
                        : 'text-white/90 hover:text-white hover:bg-white/20'
                    }`}
                  >
                    {link.name}
                  </a>
                )
              ))}
            </div>
          </nav>

          {/* Right side - Phone and CTA */}
          <div className="hidden lg:flex items-center gap-3">
            {/* Phone button with pill style */}
            <a 
              href={contact?.phoneLink || "tel:6565367364"}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                showScrolledStyle
                  ? 'bg-gray-100/80 text-gray-700 hover:bg-gray-200'
                  : 'bg-white/10 backdrop-blur-sm text-white/90 hover:bg-white/20'
              }`}
            >
              <Phone className="w-4 h-4" />
              <span>{phoneNumber}</span>
            </a>
            
            <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
              <Button
                className={`font-semibold px-6 h-10 rounded-full transition-all duration-200 ${
                  showScrolledStyle
                    ? 'bg-[#CFA935] hover:bg-[#b8942f] text-[#013C5D]'
                    : 'bg-white hover:bg-gray-100 text-[#013C5D]'
                }`}
              >
                {ctaButtonText}
              </Button>
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 rounded-lg ${
              showScrolledStyle ? 'text-[#013C5D]' : 'text-white'
            }`}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t">
          <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              link.isExternal ? (
                <Link
                  key={link.name}
                  to={link.href}
                  className="block py-2 text-gray-700 hover:text-[#CFA935] font-medium"
                >
                  {link.name}
                </Link>
              ) : (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="block py-2 text-gray-700 hover:text-[#CFA935] font-medium"
                >
                  {link.name}
                </a>
              )
            ))}
            <div className="pt-4 border-t flex flex-col gap-3">
              <a 
                href={contact?.phoneLink || "tel:6565367364"}
                className="flex items-center gap-2 text-[#013C5D] font-medium"
              >
                <Phone className="w-5 h-5" />
                {phoneNumber}
              </a>
              <a href={getWhatsAppLink()} target="_blank" rel="noopener noreferrer">
                <Button className="w-full bg-[#CFA935] hover:bg-[#b8942f] text-[#013C5D] font-semibold">
                  {ctaButtonText}
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
